/**
 * Account Service for Poker Reborn
 *
 * Handles communication with ACC (Account Cache) service.
 * Manages player balance and account state.
 *
 * See: docs/protocols/ACC.md for protocol details.
 */

import { EventEmitter } from './EventEmitter';
import { networkService } from './NetworkService';
import { GLIDS } from '@/config/network';
import { updateChips, updateGold, updateTickets, setPlayerNickname } from '@/state/AppState';
import { decodeProfile } from '@/utils/profile';
import { avatarService } from './AvatarService';
import { giftService } from './GiftService';
import { log } from '@/utils/log';

// =============================================================================
// TYPES
// =============================================================================

/** Lock info from ACC - identifies which table player is at */
export interface LockInfo {
  manager: string;
  croupier: string;
}

/** Account state from ACC */
export interface AccountState {
  username: string;
  chips: number;
  gold: number;
  tickets: number;
  lockedChips: number;
  locks: LockInfo[];
  profile: string | undefined;
  reason: string | undefined;
}

/** Account error from server (e.g. invalid_nickname) */
export interface AccountErrorInfo {
  error: string;
  message: string;
}

/** Public profile data from ACC */
export interface PublicProfileData {
  username: string;
  chips: number;
  profile: Record<string, unknown>;
  error?: string;
}

/** Account service events */
export interface AccountServiceEvents {
  /** Balance was updated */
  balanceUpdated: AccountState;

  /** Balance request started */
  balanceLoading: void;

  /** Nickname was successfully updated */
  nicknameUpdated: string;

  /** Server returned an error for a mutation */
  accountError: AccountErrorInfo;

  /** Public profile fetched for another player */
  publicProfile: PublicProfileData;

  /** Error occurred */
  error: Error;
}

// =============================================================================
// ACCOUNT SERVICE
// =============================================================================

class AccountService extends EventEmitter<AccountServiceEvents> {
  private static instance: AccountService | null = null;
  private loading = false;
  private lastState: AccountState | null = null;

  private constructor() {
    super();
    this.setupNetworkHandlers();
  }

  /** Get singleton instance */
  static getInstance(): AccountService {
    if (!AccountService.instance) {
      AccountService.instance = new AccountService();
    }
    return AccountService.instance;
  }

  /**
   * Request balance from ACC service
   * Sends "# account" command and ACC responds with full account state.
   *
   * Can be called:
   * - On login (initial balance)
   * - On reconnect (sync state)
   * - Manually (refresh)
   */
  requestBalance(): void {
    if (!networkService.isConnected()) {
      log.account.warning`Cannot request balance: not connected`;
      return;
    }

    this.loading = true;
    this.emit('balanceLoading', undefined);

    log.account.info`Requesting balance from ACC...`;
    log.account.debug`Sending to: ${GLIDS.ACCOUNT} command: # account`;
    networkService.sendCommand(GLIDS.ACCOUNT, '# account');
  }

  /** Check if balance request is in progress */
  isLoading(): boolean {
    return this.loading;
  }

  /** Get last known account state */
  getLastState(): AccountState | null {
    return this.lastState;
  }

  /**
   * Check for active session (locks) and return recovery info.
   * This should be called after authentication to restore the player
   * to their table if they were disconnected (browser refresh, etc.)
   *
   * Returns a Promise that resolves with:
   * - LockInfo if player has an active table session
   * - null if no active session (show lobby)
   */
  async checkSessionRecovery(): Promise<LockInfo | null> {
    if (!networkService.isConnected()) {
      log.account.warning`Cannot check session: not connected`;
      return null;
    }

    return new Promise((resolve) => {
      let timeoutId: ReturnType<typeof setTimeout>;

      // Set up one-time listener for account response
      const handleAccountResponse = (state: AccountState) => {
        // Clear timeout and remove listener
        clearTimeout(timeoutId);
        this.off('balanceUpdated', handleAccountResponse);

        // Check for active locks
        if (state.locks && state.locks.length > 0) {
          const lock = state.locks[0];
          if (lock) {
            log.account.info`Session recovery: found active lock manager=${lock.manager} croupier=${lock.croupier}`;
            resolve(lock);
            return;
          }
        }

        log.account.info`Session recovery: no active locks`;
        resolve(null);
      };

      // Listen for account response
      this.on('balanceUpdated', handleAccountResponse);

      // Request account info (includes locks)
      this.requestBalance();

      // Timeout after 2 seconds
      timeoutId = setTimeout(() => {
        this.off('balanceUpdated', handleAccountResponse);
        log.account.warning`Session recovery: timeout waiting for account response`;
        resolve(null);
      }, 2000);
    });
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private setupNetworkHandlers(): void {
    networkService.on('message', ({ from, body }) => {
      // Log all messages to help debug
      log.account.debug`Message received from: ${from}`;

      // Only handle messages from ACC
      if (from !== GLIDS.ACCOUNT) return;

      log.account.info`ACC message received: ${body}`;

      try {
        const message = JSON.parse(body) as Record<string, unknown>;

        // Check for account message (any reason triggers update)
        if (message.a === 'account') {
          // Handle gift errors separately (T42)
          if (message.reason === 'gift_err') {
            giftService.handleGiftError(message);
          } else if (message.reason === 'error') {
            this.handleErrorResponse(message);
          } else {
            // Success responses (including gift success — updates balance)
            this.handleAccountMessage(message);
          }
        } else if (message.a === 'public_profile') {
          this.handlePublicProfile(message);
        } else {
          log.account.info`ACC message action: ${message.a}`;
        }
      } catch {
        log.account.warning`Failed to parse ACC message: ${body}`;
      }
    });

    // Handle network errors (like "not_found" when ACC is not running)
    networkService.on('error', (error) => {
      if (this.loading) {
        log.account.error`ACC service error: ${error.message}`;
        this.loading = false;
        this.emit('error', error);
      }
    });
  }

  private handleAccountMessage(message: Record<string, unknown>): void {
    // IMPORTANT: 'chips' is ALWAYS a string from server (integer_to_binary)
    const chips = this.parseChips(message.chips);
    const gold = this.parseChips(message.gold);
    const tickets = this.parseChips(message.tickets);
    const lockedChips = Number(message.locked_chips ?? 0);

    const state: AccountState = {
      username: String(message.username ?? ''),
      chips,
      gold,
      tickets,
      lockedChips,
      locks: (message.locks as LockInfo[]) ?? [],
      profile: message.profile as string | undefined,
      reason: message.reason as string | undefined,
    };

    this.lastState = state;
    this.loading = false;

    // Update global app state
    updateChips(chips);
    updateGold(gold);
    updateTickets(tickets);

    // Decode profile and sync avatar/nickname
    log.account.info`Profile raw: ${state.profile ?? '(none)'}`;
    let decodedNickname: string | null = null;
    if (state.profile) {
      const decoded = decodeProfile(state.profile);
      log.account.info`Profile decoded: ${JSON.stringify(decoded)}`;
      if (decoded) {
        avatarService.updateFromProfile(decoded);

        decodedNickname = typeof decoded.nickname === 'string' ? decoded.nickname : null;
        setPlayerNickname(decodedNickname);
      }
    }

    // Emit for listeners
    this.emit('balanceUpdated', state);

    // Emit reason-specific events for UI feedback
    if (state.reason === 'nickname') {
      this.emit('nicknameUpdated', decodedNickname ?? '');
    }

    log.account.info`Balance updated: chips=${chips} gold=${gold} tickets=${tickets} locked=${lockedChips} reason=${state.reason}`;
  }

  private handlePublicProfile(message: Record<string, unknown>): void {
    if (message.error) {
      log.account.info`Public profile error: ${String(message.error)}`;
      this.emit('publicProfile', {
        username: String(message.username ?? ''),
        chips: 0,
        profile: {},
        error: String(message.error),
      });
      return;
    }

    const username = String(message.username ?? '');
    const chips = this.parseChips(message.chips);
    const rawProfile = message.profile;
    const profile = (typeof rawProfile === 'object' && rawProfile !== null)
      ? rawProfile as Record<string, unknown>
      : {};

    log.account.info`Public profile fetched: ${username} chips=${String(chips)}`;
    this.emit('publicProfile', { username, chips, profile });
  }

  private handleErrorResponse(message: Record<string, unknown>): void {
    const errorCode = String(message.error ?? 'unknown');
    const errorMessage = String(message.message ?? 'An error occurred');
    log.account.warning`ACC error: ${errorCode} — ${errorMessage}`;
    this.emit('accountError', { error: errorCode, message: errorMessage });
  }

  /**
   * Parse chips value - server ALWAYS sends as string
   */
  private parseChips(value: unknown): number {
    if (typeof value === 'string') {
      return parseInt(value, 10) || 0;
    }
    if (typeof value === 'number') {
      return value; // Defensive, but shouldn't happen per docs
    }
    return 0;
  }
}

/** Singleton account service instance */
export const accountService = AccountService.getInstance();
